import { Component, Input, OnInit } from '@angular/core';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { BaseControlComponent } from './base-control.component';

@Component({
    selector: 'app-text-control',
    template: `
    <div class="app-pr1 app-pb2">
      <mat-form-field
        [hintLabel]="hintLabel"
        [formGroup]="formGroup"
        class="fulltxtcnt"
        >
        <mat-label>{{ translationNeeded ? (label | translation: translationRequest) : label }}</mat-label>
        <input
          matInput
          #input
          [attr.maxlength]="maxLength"
          [placeholder]="placeHolder"
          [formControlName]="formControlName"
          [attr.disabled]="disabled"
          [class.disabled]="disabled"
          autocomplete="off"
          [mask]="mask"
          [thousandSeparator]="thousandSeparator"
          (beforeinput)="onBeforeInput($event)"
          (keydown)="onKeyDown($event)"
          (paste)="onPaste($event)"
          [type]="password ? 'password' : type"
          [min]="minTypeNumberValue"
          [max]="maxTypeNumberValue"
          [step]="step"
          (blur)="blur($event)"
          />
          @if(passwordToggle){
            <mat-icon
              matSuffix
              tabindex="0"
              [class.disabled]="disabled"
              (click)="!disabled && (password = !password)"
              (keydown.enter)="!disabled && (password = !password)"
              class="mat-datepicker-toggle text-hint-icon"
              >{{ password ? 'visibility' : 'visibility_off' }}</mat-icon
              >
            }
            @if (maxLength > 0 && IsShowLimit) {
              <mat-hint align="end"
                >{{ input.value?.length || 0 }}/{{ maxLength }}</mat-hint
                >
            }
    
            @if (textHint) {
              <mat-hint align="start">{{ textHint | translation: translationRequest }}</mat-hint>
            }
    
            @if (formControl.invalid) {
              <mat-error
                aria-atomic="true"
                aria-live="polite"
                [attr.aria-label]="label + ' ' + errorMessage | translation: translationRequest"
                >{{ errorMessage.trim() | translation: translationRequest }}</mat-error
                >
              }
            </mat-form-field>
          </div>
    `,
    styles: [
        `
      .fulltxtcnt {
        width: 100%;
      }
      .disabled {
        opacity: 0.6;
        cursor: not-allowed !important;
        pointer-events: none;
      }
      .help-icon {
        background-color: var(--sed-sedgwick-green-color);
        color: var(--sed-dynamic-black-color);
        border: 5px solid var(--sed-sedgwick-green-color);
        border-radius: 2px;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0 !important;
        margin-right: 5px !important;
      }
    `,
    ],
    standalone: false
})
export class TextControlComponent
  extends BaseControlComponent
  implements OnInit
{
  
  @Input()
  maxLength: number;

  @Input()
  mask: string;

  @Input()
  thousandSeparator: string = ' ';

  @Input()
  password: boolean = false;

  @Input()
  passwordToggle: boolean = false;

  @Input()
  type: string = 'text';

  @Input()
  textHint: string;

  @Input()
  IsShowLimit: boolean = true;

  @Input()
  minTypeNumberValue: number;

  @Input()
  maxTypeNumberValue: number;

  @Input()
  step: number;

  @Input()
  disabled: boolean = false;

  /** Shown when a letter is typed into a numeric mask, which would otherwise drop it silently. */
  @Input()
  rejectNonNumericMessage: string;

  private nonNumericRejected = false;
  private valueAtRejection: unknown = undefined;

  translationRequest = new TranslationRequest({
        language: sessionStorage.getItem('languagepreference'),
        key: null,
        contNum: 0,
        tableName: null,
        primaryKey: null,
        primaryValue: null,
        lob: null,
        group: 'Shared',
        subGroup: 'Shared',
      });

  @Input()
  translationNeeded :boolean = true;

  override get errorMessage(): string {
    const numericOnly = this.formControl?.errors?.['numericOnly'];
    if (typeof numericOnly === 'string' && numericOnly.length > 0) {
      return numericOnly;
    }
    return super.errorMessage;
  }

  ngOnInit() {
    if (!this.rejectNonNumericMessage) {
      return;
    }

    this.formControl.addValidators(() =>
      this.nonNumericRejected
        ? { numericOnly: this.rejectNonNumericMessage }
        : null,
    );

    this.subs.sink = this.formControl.valueChanges.subscribe((value) => {
      if (!this.nonNumericRejected || this.containsLetter(value)) {
        return;
      }
      if (Object.is(value, this.valueAtRejection)) {
        return;
      }
      this.nonNumericRejected = false;
      this.formControl.updateValueAndValidity();
    });
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.rejectNonNumericMessage || this.disabled) {
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (event.key.length === 1 && this.containsLetter(event.key)) {
      event.preventDefault();
      this.rejectAlpha();
    }
  }

  onBeforeInput(event: InputEvent) {
    if (!this.rejectNonNumericMessage || this.disabled) {
      return;
    }
    const data = event.data ?? event.dataTransfer?.getData('text') ?? '';
    if (data && this.containsLetter(data)) {
      event.preventDefault();
      this.rejectAlpha();
    }
  }

  onPaste(event: ClipboardEvent) {
    if (!this.rejectNonNumericMessage || this.disabled) {
      return;
    }
    const text = event.clipboardData?.getData('text') ?? '';
    if (this.containsLetter(text)) {
      event.preventDefault();
      this.rejectAlpha();
    }
  }

  blur(event: any) {
    event.target.value = event.target.value?.trim();
    if (this.step === 1) {
      event.target.value = parseInt(event.target.value);
    }
  }

  private rejectAlpha() {
    this.valueAtRejection = this.formControl.value;
    this.nonNumericRejected = true;
    this.formControl.updateValueAndValidity({ emitEvent: false });
    this.formControl.markAsTouched();
  }

  private containsLetter(value: unknown): boolean {
    return /\p{L}/u.test(String(value ?? ''));
  }
}
